/**
 * Asset materials (files and links) and the catalog file uploads behind them. Every material write sends the
 * asset revision and answers the next one; the asset query is refreshed after each, so the page reads the new list.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, apiUpload } from '../client';
import { statusApiFilesStatusGetParamsIdsMax } from '../generated/schemas';
import type { CatalogFileOut, FileStatusOut, MaterialIn, MaterialPatch, MaterialSaveOut } from '../types/materials';
import type { RevisionOut } from '../types';

/** The file status route answers at most this many ids at once. */
export const FILE_STATUS_MAX = statusApiFilesStatusGetParamsIdsMax;
/** Text extraction is short; the dialog asks again every 2 s while any file is still pending. */
export const FILE_STATUS_POLL_MS = 2000;
export const UPLOAD_TOO_LARGE = 'A file can be at most 50 MB.';

export const materialKeys = {
  fileStatus: (ids: string[]) => ['files', 'status', ids] as const,
  text: (assetId: string, materialId: string) => ['assets', 'material-text', assetId, materialId] as const,
};

/** The material's bytes: a PDF opens inline, Markdown comes as text, anything else downloads. */
export function materialFileUrl(assetId: string, materialId: string): string {
  return `/api/assets/${assetId}/materials/${materialId}/file`;
}

/** A material write changes the asset (its materials and revision) and its history. */
function useRefreshAsset(assetId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['assets', 'detail', assetId] });
    void client.invalidateQueries({ queryKey: ['assets', 'history', assetId] });
  };
}

/** A stale revision (409) means someone else saved meanwhile: read the asset again so the next try can pass. */
function useRefreshOnConflict(assetId: string) {
  const refresh = useRefreshAsset(assetId);
  return (error: Error) => {
    if (error instanceof ApiError && error.status === 409) refresh();
  };
}

/** One catalog file to `POST /api/files`. The dialog shows each file's outcome on its row, so no toast. */
export function useUploadCatalogFile() {
  return useMutation({
    meta: { quiet: true },
    mutationFn: (file: File) => apiUpload<CatalogFileOut>('/api/files', file, UPLOAD_TOO_LARGE),
  });
}

/** The text status of up to ten uploads, asked again every 2 s until none is pending. */
export function useFileStatuses(ids: string[]) {
  const wanted = ids.slice(0, FILE_STATUS_MAX);
  return useQuery({
    queryKey: materialKeys.fileStatus(wanted),
    queryFn: () =>
      api<FileStatusOut[]>(`/api/files/status?${wanted.map((id) => `ids=${encodeURIComponent(id)}`).join('&')}`),
    enabled: wanted.length > 0,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((s) => s.text_status === 'pending') ? FILE_STATUS_POLL_MS : false,
  });
}

export function useAddMaterial(assetId: string) {
  const refresh = useRefreshAsset(assetId);
  const conflict = useRefreshOnConflict(assetId);
  return useMutation({
    meta: { success: (answer: MaterialSaveOut) => `${answer.material.title} added` },
    mutationFn: (body: MaterialIn) => api<MaterialSaveOut>(`/api/assets/${assetId}/materials`, 'POST', body),
    onSuccess: refresh,
    onError: conflict,
  });
}

export function useUpdateMaterial(assetId: string) {
  const refresh = useRefreshAsset(assetId);
  const conflict = useRefreshOnConflict(assetId);
  return useMutation({
    meta: { success: (answer: MaterialSaveOut) => `${answer.material.title} saved` },
    mutationFn: ({ id, change }: { id: string; change: MaterialPatch }) =>
      api<MaterialSaveOut>(`/api/assets/${assetId}/materials/${id}`, 'PATCH', change),
    onSuccess: refresh,
    onError: conflict,
  });
}

export function useRemoveMaterial(assetId: string) {
  const refresh = useRefreshAsset(assetId);
  const conflict = useRefreshOnConflict(assetId);
  return useMutation({
    meta: { success: 'Material removed' },
    mutationFn: ({ id, revision }: { id: string; revision: number }) =>
      api<RevisionOut>(`/api/assets/${assetId}/materials/${id}?revision=${revision}`, 'DELETE'),
    onSuccess: refresh,
    onError: conflict,
  });
}

async function fetchText(path: string): Promise<string> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { Accept: 'text/plain', 'X-Requested-With': 'wynxx-discover' },
  });
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new CustomEvent('wynxx:unauthorized'));
    const payload = (await response.json().catch(() => null)) as { detail?: unknown } | null;
    const detail = typeof payload?.detail === 'string' ? payload.detail : `Request failed (${response.status}).`;
    throw new ApiError(response.status, detail);
  }
  return response.text();
}

/** A Markdown material's text, for the preview dialog (the server sends it as `text/plain`). */
export function useMaterialText(assetId: string, materialId: string) {
  return useQuery({
    queryKey: materialKeys.text(assetId, materialId),
    queryFn: () => fetchText(materialFileUrl(assetId, materialId)),
  });
}
